/**
 * Asking the server for a range of rows.
 *
 * The only I/O in the row engine, and deliberately the whole of it: everything
 * above this file is pure buffer algebra, and everything below is `openapi-fetch`.
 * The store takes a `RowFetcher` as a parameter rather than importing this one,
 * so its tests drive it with a function that resolves whatever they like and
 * never touch the network.
 *
 * Rows are not TanStack Query, and this is the file that shows why. The buffer is
 * one mutable projection assembled from overlapping paginated reads, mutated in
 * place by realtime events and optimistic edits; `useInfiniteQuery` only grows at
 * the ends, never evicts a middle page, and has no way to express "insert at index
 * 812 and shift the rest". Deduplication here is range coalescing, not key
 * equality. So there is no `rows` query key, and this fetches directly.
 */
import { getApi, unwrap } from '@/api/client'
import type { RowData } from '@/api/types'
import type { GroupByMetadata } from '../grid/grouping'
import type { RowRange } from './rowRange'
import type { RowQuery } from './rowQuery'

export interface RowPage {
  rows: RowData[]
  /**
   * How many rows each group holds, for the groups this page touches.
   *
   * Undefined when the response said nothing, which is what an ungrouped view
   * answers -- and, like the count, is not the same as an empty answer. The
   * window keeps what it was told before rather than forgetting the sizes of
   * groups it is still showing.
   */
  groupByMetadata?: GroupByMetadata
  /**
   * Total rows in the view.
   *
   * Undefined when the response omitted it, which is what `exclude_count` asks
   * for. The window then keeps the count it already had rather than adopting a
   * zero it was never told.
   */
  count: number | undefined
}

/** What the store needs from the network, and all it needs. */
export type RowFetcher = (
  query: RowQuery,
  range: RowRange,
  signal: AbortSignal,
) => Promise<RowPage>

/** The shape the grid view endpoint answers with, narrowed to what is read. */
interface GridRowsResponse {
  count?: number | null
  results?: unknown[]
  group_by_metadata?: GroupByMetadata
}

/**
 * Fetches one range of rows from a view.
 *
 * `limit`/`offset` rather than `page`/`size`, because the window is a range of
 * absolute row indexes and not a page number -- the endpoint supports both, and
 * only one of them can express "rows 812 to 852".
 *
 * Filters and sortings are not sent. The endpoint applies the view's own,
 * server-side, which is why they appear in the query's signature but not in its
 * request parameters: the signature describes the answer, the parameters describe
 * the ask. (Publicly shared views serialise them explicitly, because the reader
 * there may have chosen a filter the view does not hold. That path arrives with
 * the public shells.)
 */
const fetchFromApi: RowFetcher = async (query, range, signal) => {
  const body = await unwrap<GridRowsResponse>(
    getApi().GET('/api/database/views/grid/{view_id}/', {
      params: {
        // A number by construction: this fetcher is only ever given a view the
        // reader is signed in to. A publicly shared view is addressed by slug
        // and has a fetcher of its own.
        path: { view_id: Number(query.viewId) },
        query: {
          limit: Math.max(0, range.end - range.start),
          offset: range.start,
          include: 'row_metadata',
          ...(query.search.term === ''
            ? {}
            : { search: query.search.term, search_mode: query.search.mode }),
        },
      },
      signal,
    }),
  )

  return {
    rows: (body.results ?? []) as RowData[],
    // Null and absent both mean "not told", which is not the same as zero.
    count: body.count ?? undefined,
    // Sent by this endpoint whenever the view is grouped, without being asked
    // for -- the server already had to order by those fields to answer at all.
    ...(body.group_by_metadata === undefined
      ? {}
      : { groupByMetadata: body.group_by_metadata }),
  }
}

/**
 * The fetcher every row window uses, swappable for a stub.
 *
 * The same seam as `__resetRealtimeForTests`, and for a sharper reason than
 * convenience: jsdom brings its own `AbortController`, Node brings `Request`,
 * and Node refuses a signal that is not one of its own -- so under jsdom a real
 * request carrying an abort signal cannot be built at all. Every request this
 * makes carries one, so a component test that reached the network would not fail
 * on the network, it would fail on the environment. In a browser the two are the
 * same implementation and the question does not arise, which is why the fetcher's
 * own spec runs in the node environment and exercises the real thing.
 */
let fetcher: RowFetcher = fetchFromApi

export const fetchViewRows: RowFetcher = (query, range, signal) =>
  fetcher(query, range, signal)

/** Test seam: hand the row windows a fetcher, or `null` to restore the real one. */
export function __setRowFetcherForTests(replacement: RowFetcher | null): void {
  fetcher = replacement ?? fetchFromApi
}
